/**
 * BulkDeckMarkerLayer — WebGL marker renderer for bulk action dialog maps.
 *
 * Renders stop pill markers via deck.gl IconLayer + TextLayer when the total
 * marker count exceeds the DOM threshold. Reuses the same pill-shell generator
 * as the route manager's DeckMarkerRenderer for visual consistency.
 *
 * Must be rendered inside a @vis.gl/react-google-maps <Map> component.
 */
/* eslint-disable no-undef -- `google` is the Maps JS SDK global */
import { IconLayer, TextLayer } from '@deck.gl/layers'
import { useTheme } from '@mui/material/styles'
import { useMap } from '@vis.gl/react-google-maps'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DeckOverlay } from 'components/common/map/webgl/DeckOverlay'
import { hexToRgb } from 'components/common/map/webgl/generators/colorUtils'
import {
  generatePillHalo,
  generatePillShell,
  getPillInnerWidth,
  PILL_CSS_HEIGHT,
  PILL_TEXT_PIXEL_OFFSET_Y,
} from 'components/common/map/webgl/generators/generatePillIcon'
import { useLabelDensity } from 'components/common/map/webgl/useLabelDensity'
import { useMarkerDensity } from 'components/common/map/webgl/useMarkerDensity'
import { getMaxTextureSize } from 'components/common/map/webgl/useWebGLSupport'
import { resolveMarkerColors, parseStopFeature } from 'utils/markerColorUtils'

/**
 * Like hexToRgb but returns [r, g, b, a] (a = 0-255), preserving alpha from
 * rgba() strings such as those returned by MUI getContrastText.
 */
function colorToRgba(color) {
  if (!color || typeof color !== 'string') return [128, 128, 128, 255]
  const rgbaMatch = color.match(/rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)/)
  if (rgbaMatch) {
    return [parseInt(rgbaMatch[1], 10), parseInt(rgbaMatch[2], 10), parseInt(rgbaMatch[3], 10), Math.round(parseFloat(rgbaMatch[4]) * 255)]
  }
  const [r, g, b] = hexToRgb(color)
  return [r, g, b, 255]
}

const DEFAULT_LABEL_ZOOM = 15

// [west, south, east, north] — the shape useLabelDensity destructures.
const readViewBounds = map => {
  const bounds = map?.getBounds?.()
  if (!bounds) return null
  const sw = bounds.getSouthWest()
  const ne = bounds.getNorthEast()
  return [sw.lng(), sw.lat(), ne.lng(), ne.lat()]
}

// Module scope so the density hooks' memo deps stay stable across renders.
const getItemId = item => item.id
const getItemPosition = item => item.position

// Shell cache — one entry per (innerColor, outerColor, pillInnerWidth) tuple
const shellCache = new Map()

function shellCacheKey(inner, outer, pillInnerWidth, outlineColor) {
  return `${inner}|${outer}|${pillInnerWidth}|${outlineColor ?? ''}`
}

function getShellIcon(innerColor, outerColor, pillInnerWidth, outlineColor) {
  const key = shellCacheKey(innerColor, outerColor, pillInnerWidth, outlineColor)
  const cached = shellCache.get(key)
  if (cached) return cached
  const { canvas } = generatePillShell(innerColor, outerColor, pillInnerWidth, outlineColor)
  const pw = canvas.width
  const ph = canvas.height
  const entry = { canvas, width: pw, height: ph, anchorX: pw / 2, anchorY: ph }
  shellCache.set(key, entry)
  return entry
}

// Halo shell cache — one entry per (haloColor, pillInnerWidth) tuple. Centre-
// anchored so the halo IconLayer can offset it onto the pill body with the same
// PILL_TEXT_PIXEL_OFFSET_Y the labels use. cssHeight is the natural CSS height
// (getSize) — constant per pillInnerWidth, so the layer renders it at 1:1 scale.
const haloCache = new Map()

function getHaloIcon(haloColor, pillInnerWidth) {
  const key = `${haloColor}|${pillInnerWidth}`
  const cached = haloCache.get(key)
  if (cached) return cached
  const { canvas, height: cssHeight } = generatePillHalo(haloColor, pillInnerWidth)
  const pw = canvas.width
  const ph = canvas.height
  const entry = { canvas, width: pw, height: ph, anchorX: pw / 2, anchorY: ph / 2, cssHeight }
  haloCache.set(key, entry)
  return entry
}

function resolveColors(icon, properties) {
  const { bgColor, borderColor, textColor } = resolveMarkerColors(icon, properties)
  return { innerColor: bgColor, outerColor: borderColor, textColor }
}

// Row-packing atlas builder (simplified from DeckMarkerRenderer)
function packAtlas(entries) {
  if (entries.length === 0) {
    const c = document.createElement('canvas')
    c.width = 1
    c.height = 1
    return { iconAtlas: c, iconMapping: {} }
  }

  // Clamp to what the driver accepts — see useStopPillLayers for why.
  const MAX_DIM = Math.min(4096, getMaxTextureSize() || 4096)
  let curX = 0
  let curY = 0
  let rowH = 0
  let totalW = 0

  entries.forEach(e => {
    if (curX + e.ic.width > MAX_DIM) {
      curY += rowH
      curX = 0
      rowH = 0
    }
    e.x = curX
    e.y = curY
    curX += e.ic.width
    totalW = Math.max(totalW, curX)
    rowH = Math.max(rowH, e.ic.height)
  })

  const totalH = curY + rowH
  const atlas = document.createElement('canvas')
  atlas.width = Math.min(totalW, MAX_DIM)
  atlas.height = totalH
  const ctx = atlas.getContext('2d')
  const iconMapping = {}

  entries.forEach(e => {
    ctx.drawImage(e.ic.canvas, e.x, e.y)
    iconMapping[e.key] = {
      x: e.x,
      y: e.y,
      width: e.ic.width,
      height: e.ic.height,
      anchorX: e.ic.anchorX,
      anchorY: e.ic.anchorY,
    }
  })

  return { iconAtlas: atlas, iconMapping }
}

// Selection ADDS an outer ring (the halo) outside the existing pill — the
// chip's own outer/route colour and inner fill are unchanged. Callers key
// highlightedStopIds by EITHER the stop's `id` (daily maps = workOrderId) or its
// configuredServiceEventId (master = event id), so match on both — keying by
// only one silently drops the halo on the surface that uses the other.
const resolveHighlightColor = (highlightedStopIds, properties) => {
  if (!highlightedStopIds) return null
  return highlightedStopIds.get(properties?.id) ?? highlightedStopIds.get(properties?.configuredServiceEventId) ?? null
}

// Hook form: returns the deck.gl layers (icon + label) without mounting a
// DeckOverlay. Lets callers compose pill markers into a shared overlay
// alongside other layers (containers, driver path) so the map only owns one
// GoogleMapsOverlay canvas — multiple stacked canvases blink during smooth
// zoom when Google Maps fires renderingtype_changed across them.
export function useBulkDeckMarkerLayers({
  features = [],
  onClick,
  onHover,
  labelDensityLevel = 0,
  totalStopCount = 0,
  highlightedStopIds = null,
  idPrefix = 'bulk-stop',
}) {
  const map = useMap()
  // Tracked here rather than threaded from each caller so the per-pan re-render
  // stays inside this layer instead of the whole map.
  const [zoom, setZoom] = useState(() => map?.getZoom?.() ?? DEFAULT_LABEL_ZOOM)
  const [viewBounds, setViewBounds] = useState(() => readViewBounds(map))
  useEffect(() => {
    if (!map) return undefined
    const syncCamera = () => {
      const nextZoom = map.getZoom?.()
      if (typeof nextZoom === 'number') setZoom(nextZoom)
      const nextBounds = readViewBounds(map)
      if (nextBounds) setViewBounds(nextBounds)
    }
    syncCamera()
    const zoomListener = map.addListener('zoom_changed', syncCamera)
    const boundsListener = map.addListener('bounds_changed', syncCamera)
    return () => {
      google.maps.event.removeListener(zoomListener)
      google.maps.event.removeListener(boundsListener)
    }
  }, [map])

  const pillInnerWidth = useMemo(() => getPillInnerWidth(totalStopCount), [totalStopCount])
  const muiTheme = useTheme()
  const themeOutlineColor = muiTheme?.palette?.mode === 'dark' ? muiTheme.palette.common.white : null

  const stopItems = useMemo(() => {
    const items = features
      .map(feature => {
        const parsed = parseStopFeature(feature)
        if (!parsed) return null

        const { lng, lat, properties, icon, label, id } = parsed
        const colors = resolveColors(icon, properties)
        // A selected stop keeps its own pill colours untouched — selection reads
        // as a detached halo RING (drawn with a gap outside the pill), never a
        // recolour or size change on the marker itself (consistent across bulk
        // actions; honours the "no recolour on active" rule).
        const haloColor = resolveHighlightColor(highlightedStopIds, properties)
        const { outerColor } = colors
        const outlineColor = themeOutlineColor
        const iconKey = shellCacheKey(colors.innerColor, outerColor, pillInnerWidth, outlineColor)
        // Stack order via array index: 0 baseline, 1 staged, 2 unassigned.
        const isStaged = get(properties, '_isStagedForMove') === true
        const isUnassigned = icon === 'unassigned-marker' || icon === 'to-be-assigned-marker'
        let stackRank = 0
        if (isUnassigned) stackRank = 2
        else if (isStaged) stackRank = 1

        return {
          id,
          position: [lng, lat],
          label,
          feature,
          ...colors,
          outerColor,
          outlineColor,
          haloColor,
          haloIconKey: haloColor ? `${haloColor}|${pillInnerWidth}` : null,
          iconKey,
          stackRank,
        }
      })
      .filter(Boolean)
    items.sort((a, b) => a.stackRank - b.stackRank)
    return items
  }, [features, pillInnerWidth, themeOutlineColor, highlightedStopIds])

  // Stops the dispatcher is acting on must survive marker culling regardless of
  // how crowded their cell is.
  const keepIds = useMemo(() => new Set(stopItems.filter(item => item.stackRank > 0 || item.haloColor).map(item => item.id)), [stopItems])

  const visibleStopItems = useMarkerDensity({
    items: stopItems,
    zoom,
    keepIds,
    getId: getItemId,
    getPosition: getItemPosition,
  })

  // Shared with every other pill map so "Label spacing" behaves identically
  // everywhere; its collision radius is screen-space, hence zoom + bounds.
  // Keyed on the full set, not the culled one: the hook forces collision past a
  // label count, and culling first would drop under it and show every label.
  const visibleLabelIds = useLabelDensity({
    items: stopItems,
    bounds: viewBounds,
    zoom,
    level: labelDensityLevel,
    textOffsetY: PILL_TEXT_PIXEL_OFFSET_Y,
  })

  const labelItems = useMemo(() => visibleStopItems.filter(item => visibleLabelIds.has(item.id)), [visibleStopItems, visibleLabelIds])

  const shellSources = useMemo(() => {
    const uniqueKeys = new Map()
    stopItems.forEach(item => {
      if (!uniqueKeys.has(item.iconKey)) {
        uniqueKeys.set(item.iconKey, { innerColor: item.innerColor, outerColor: item.outerColor, outlineColor: item.outlineColor })
      }
    })
    return uniqueKeys
  }, [stopItems])

  // Keyed on the distinct shell set, not item identity — repacking on a fresh
  // `stopItems` with unchanged colours forces a full deck.gl texture re-upload.
  const atlasSignature = useMemo(() => Array.from(shellSources.keys()).sort().join('~'), [shellSources])
  const shellSourcesRef = useRef(shellSources)
  shellSourcesRef.current = shellSources

  const { iconAtlas, iconMapping } = useMemo(() => {
    const entries = Array.from(shellSourcesRef.current.entries()).map(([key, c]) => ({
      key,
      ic: getShellIcon(c.innerColor, c.outerColor, pillInnerWidth, c.outlineColor),
    }))
    return packAtlas(entries)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- shells are read through a ref; the signature is the real dependency
  }, [atlasSignature, pillInnerWidth])

  // Separate atlas for the selection halos (pill-shaped outline rings). haloSize
  // is the natural CSS height shared by every halo — the layer's getSize.
  const { haloAtlas, haloMapping, haloSize } = useMemo(() => {
    const uniqueKeys = new Map()
    let cssHeight = PILL_CSS_HEIGHT
    stopItems.forEach(item => {
      if (item.haloIconKey && !uniqueKeys.has(item.haloIconKey)) {
        const ic = getHaloIcon(item.haloColor, pillInnerWidth)
        cssHeight = ic.cssHeight
        uniqueKeys.set(item.haloIconKey, ic)
      }
    })
    const entries = Array.from(uniqueKeys.entries()).map(([key, ic]) => ({ key, ic }))
    const { iconAtlas: atlas, iconMapping: mapping } = packAtlas(entries)
    return { haloAtlas: atlas, haloMapping: mapping, haloSize: cssHeight }
  }, [stopItems, pillInnerWidth])

  const handleClick = useCallback(
    info => {
      if (info.object?.feature && onClick) {
        onClick(info.object.feature)
      }
    },
    [onClick]
  )

  const handleHover = useCallback(
    info => {
      if (info.object && onHover) {
        const [lng, lat] = info.object.position
        onHover({ lat, lng }, get(info.object, 'id'))
      } else if (!info.object && onHover) {
        onHover(null, null)
      }
    },
    [onHover]
  )

  return useMemo(() => {
    if (visibleStopItems.length === 0) return []

    // Selection halo — a pill-SHAPED outline ring drawn OUTSIDE each selected
    // pill with a gap (constant screen size, so it reads the same at any zoom).
    // Centre-anchored, then offset onto the pill body with PILL_TEXT_PIXEL_OFFSET_Y
    // so the ring hugs the marker's rounded-rect silhouette instead of a circle.
    const haloItems = visibleStopItems.filter(d => d.haloIconKey)
    const haloVersion = haloItems.map(d => `${d.id}:${d.haloIconKey}`).join('|')
    const haloLayer = new IconLayer({
      id: `${idPrefix}-halo`,
      data: haloItems,
      getPosition: d => d.position,
      getIcon: d => d.haloIconKey,
      getSize: haloSize,
      getPixelOffset: [0, PILL_TEXT_PIXEL_OFFSET_Y],
      iconAtlas: haloAtlas,
      iconMapping: haloMapping,
      sizeScale: 1,
      sizeUnits: 'pixels',
      pickable: false,
      parameters: { depthTest: false },
      updateTriggers: { getIcon: haloVersion, getPosition: haloVersion },
    })

    const iconLayer = new IconLayer({
      id: `${idPrefix}-icons`,
      data: visibleStopItems,
      getPosition: d => d.position,
      getIcon: d => d.iconKey,
      getSize: PILL_CSS_HEIGHT,
      iconAtlas,
      iconMapping,
      sizeScale: 1,
      sizeUnits: 'pixels',
      pickable: true,
      onClick: handleClick,
      onHover: handleHover,
      parameters: { depthTest: false },
    })

    const labelVersion = labelItems.map(d => `${d.id}:${d.label}`).join('|')

    const textLayer = new TextLayer({
      id: `${idPrefix}-labels`,
      data: labelItems,
      getPosition: d => d.position,
      getText: d => d.label,
      getSize: 13,
      getColor: d => colorToRgba(d.textColor),
      getPixelOffset: [0, PILL_TEXT_PIXEL_OFFSET_Y],
      fontFamily: '"Inter", sans-serif',
      fontWeight: 900,
      sizeUnits: 'pixels',
      getTextAnchor: 'middle',
      getAlignmentBaseline: 'center',
      pickable: false,
      parameters: { depthTest: false },
      updateTriggers: {
        getText: labelVersion,
        getPosition: labelVersion,
      },
    })

    return [haloLayer, iconLayer, textLayer]
  }, [visibleStopItems, labelItems, iconAtlas, iconMapping, haloAtlas, haloMapping, haloSize, handleClick, handleHover, idPrefix])
}

const BulkDeckMarkerLayer = ({ features, onClick, onHover, labelDensityLevel, totalStopCount, highlightedStopIds }) => {
  const layers = useBulkDeckMarkerLayers({ features, onClick, onHover, labelDensityLevel, totalStopCount, highlightedStopIds })
  // zIndex 10 keeps markers above sibling DeckOverlays (e.g. the cached-directions
  // overlay at zIndex 5) without depending on JSX render order.
  // floatPane lifts the marker canvas above the native sequence/driver polylines
  // (which live in the overlay panes), so the lines sit BELOW the markers.
  return <DeckOverlay verifyRender componentName="BulkDeckMarkerLayer" layers={layers} pane="floatPane" style={{ zIndex: 10 }} />
}

BulkDeckMarkerLayer.propTypes = {
  features: PropTypes.array,
  onClick: PropTypes.func,
  onHover: PropTypes.func,
  labelDensityLevel: PropTypes.number,
  totalStopCount: PropTypes.number,
  highlightedStopIds: PropTypes.instanceOf(Map),
}

export default BulkDeckMarkerLayer
